import React, { lazy, useCallback, useEffect } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';

import {
  buildPublicSurfacePath,
  resolvePublicSurfacePath,
} from '../../config/publicSurfaces';
import {
  resolveFlowMeta,
  resolvePublicFlowFromSearchParams,
} from '../../config/reportConversionFlowRegistry';
import {
  ReportConversionFlowProvider,
  useReportConversionFlow,
} from '../../contexts/ReportConversionFlowContext';
import { syncLandingMessageAttribution } from '../../utils/utm';
import { HERO_POSTERS } from './landing-sections/heroMedia';

const DefaultReportLandingPage = lazy(() => import('../../pages/DefaultLandingPage'));

// This wrapper is in the ENTRY chunk while DefaultLandingPage is lazy — so kicking
// off the hero poster fetch here means the landing's LCP image downloads in
// parallel with the page's JS chunk instead of after it. 65-95KB, /report* only.
let heroPosterPreloaded = false;
function preloadHeroPoster() {
  if (heroPosterPreloaded || typeof document === 'undefined') return;
  heroPosterPreloaded = true;
  const link = document.createElement('link');
  link.rel = 'preload';
  link.as = 'image';
  link.href = HERO_POSTERS[0];
  link.fetchPriority = 'high';
  document.head.appendChild(link);
}

const MESSAGE_QUERY_KEYS = [
  'messageKey',
  'message_key',
  'messageKeyApplied',
  'message_key_applied',
];

function withoutMessageQueryParams(search = '') {
  const params = new URLSearchParams(search);
  MESSAGE_QUERY_KEYS.forEach((key) => params.delete(key));
  const nextSearch = params.toString();
  return nextSearch ? `?${nextSearch}` : '';
}

function ReportMarketingRouteContent({
  LandingPageComponent = DefaultReportLandingPage,
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const { flowKey: appliedFlowKey, flowVersion: appliedFlowVersion } =
    useReportConversionFlow();
  const resolvedRoute = resolvePublicSurfacePath(location.pathname);
  const safeSearch = withoutMessageQueryParams(location.search);
  const implementationMessageKey = resolvedRoute?.implementationMessageKey;
  const isRejectedRoute = !resolvedRoute;

  useEffect(() => {
    if (isRejectedRoute) {
      syncLandingMessageAttribution();
      return;
    }

    syncLandingMessageAttribution({
      requestedMessageKey: resolvedRoute.messageKey,
      appliedMessageKey: resolvedRoute.messageKeyApplied,
      productKey: resolvedRoute.productKey,
      cartridgeKey: resolvedRoute.cartridgeKey,
      surfaceKey: resolvedRoute.surfaceKey,
      experienceDisplayName: resolvedRoute.experienceDisplayName,
      templateId: resolvedRoute.templateId,
      landingId: resolvedRoute.landingId,
      destinationId: resolvedRoute.destinationId,
      editionId: resolvedRoute.editionId,
      renderEngine: resolvedRoute.renderEngine,
      flowKey: appliedFlowKey,
      flowVersion: appliedFlowVersion,
    });
  }, [
    appliedFlowKey,
    appliedFlowVersion,
    isRejectedRoute,
    location.pathname,
    resolvedRoute?.cartridgeKey,
    resolvedRoute?.destinationId,
    resolvedRoute?.editionId,
    resolvedRoute?.experienceDisplayName,
    resolvedRoute?.landingId,
    resolvedRoute?.messageKey,
    resolvedRoute?.messageKeyApplied,
    resolvedRoute?.productKey,
    resolvedRoute?.renderEngine,
    resolvedRoute?.surfaceKey,
    resolvedRoute?.templateId,
  ]);

  const handleCampaignKeyChange = useCallback((nextImplementationMessageKey) => {
    const nextPath = buildPublicSurfacePath({
      productKey: 'adu_lab',
      surfaceKey: 'report',
      implementationMessageKey: nextImplementationMessageKey,
    });
    if (!nextPath) return;

    navigate({
      pathname: nextPath,
      search: safeSearch,
      hash: location.hash,
    });
  }, [location.hash, navigate, safeSearch]);

  if (isRejectedRoute) return null;

  if (!resolvedRoute.isCanonicalPath || safeSearch !== location.search) {
    return (
      <Navigate
        to={{
          pathname: resolvedRoute.canonicalPath,
          search: safeSearch,
          hash: location.hash,
        }}
        replace
      />
    );
  }

  return React.createElement(LandingPageComponent, {
    campaignKey: implementationMessageKey,
    onCampaignKeyChange: handleCampaignKeyChange,
  });
}

export function ReportMarketingRoute(props) {
  const location = useLocation();
  const initialFlow = resolvePublicFlowFromSearchParams(location.search);
  const initialFlowVersion = resolveFlowMeta(initialFlow).flowVersion;

  // Fire-and-forget after commit (render-pure); module-guarded, so repeated
  // renders/navigation are no-ops. Not at module top level: this module sits
  // in the ENTRY chunk, and the poster should only preload on /report* mounts.
  useEffect(() => {
    preloadHeroPoster();
  }, []);

  return (
    <ReportConversionFlowProvider
      key={`${initialFlow}:${initialFlowVersion}`}
      initialFlow={initialFlow}
    >
      <ReportMarketingRouteContent {...props} />
    </ReportConversionFlowProvider>
  );
}

export default ReportMarketingRoute;
